// The HTTP lobby/session client (design D4): create/join/claim/ready/start/cancel and
// roster/config poll over `waggle-server`'s HTTP handlers — NOT the WebSocket protocol.
// The server issues a 128-bit session token once (MATCH-LIFECYCLE §1); the client stores
// it as the credential for the in-match `Hello` and every mutating request.

/** Host-editable match config (UI.md §6; MATCH-LIFECYCLE §2 Lobby). */
export interface LobbyConfig {
  durationMinutes: number;
  seasonArc: string;
  colonyCount: number;
  beekeeperEnabled: boolean;
}

/** A player's readiness in the lobby roster. */
export type ReadyStatus = 'ready' | 'picking';

/** One roster row (UI.md §6 lobby roster). */
export interface RosterEntry {
  name: string;
  colonyName: string | null;
  status: ReadyStatus;
  isHost: boolean;
}

/** The lobby-phase state (MATCH-LIFECYCLE §2), the source for the lobby screen. */
export interface LobbyState {
  matchId: string;
  matchCode: string;
  players: RosterEntry[];
  capacity: number;
  botFill: number;
  config: LobbyConfig;
  /** Whether the local session is the host (drives host-only affordances). */
  isHost: boolean;
}

/** A session credential returned by create/join. */
export interface Session {
  token: string;
  matchId: string;
}

export interface LobbyClientDeps {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

/** Thrown when a `LobbyClient` HTTP request fails (non-2xx) — never a bare string
 * throw, so callers can inspect the status. */
export class LobbyHttpError extends Error {
  constructor(
    public readonly method: string,
    public readonly path: string,
    public readonly status: number,
  ) {
    super(`LobbyClient: ${method} ${path} failed with status ${status}`);
    this.name = 'LobbyHttpError';
  }
}

/** A thin `fetch` client for `waggle-server`'s lobby/session HTTP handlers (design
 * D4). Every op below is a plain HTTP request over the injected `fetchImpl` (real
 * `fetch` in production, a scripted fake in tests) — none goes over the WebSocket
 * protocol, which begins only once the match is `Running` (MATCH-LIFECYCLE §2). */
export class LobbyClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private _token: string | null = null;
  private _matchId: string | null = null;

  constructor(deps: LobbyClientDeps) {
    this.baseUrl = deps.baseUrl.replace(/\/+$/, '');
    // Bind only the *default* native `fetch` — a real browser's `fetch` performs a
    // receiver brand-check and throws "Illegal invocation" when later invoked as
    // `this.fetchImpl(...)` (a method call whose `this` is this `LobbyClient`
    // instance, not `window`). An injected test `fetchImpl` (`vi.fn()` etc.)
    // performs no such check, so it is left un-bound and called exactly as before.
    this.fetchImpl = deps.fetchImpl ?? fetch.bind(globalThis);
  }

  /** The stored session token — the credential for every request past create/join,
   * never the display name (MATCH-LIFECYCLE §1). */
  get token(): string | null {
    return this._token;
  }

  /** Restores a previously issued session (e.g. rehydrating from the client store on
   * a re-render) without re-creating/re-joining the match. */
  setSession(session: Session): void {
    this.adoptSession(session);
  }

  /** Host creates a new match with the given config. */
  async create(
    name: string,
    config: LobbyConfig,
    opponent?: 'auto' | 'human',
  ): Promise<{ session: Session; lobby: LobbyState }> {
    const result = await this.request<{ session: Session; lobby: LobbyState }>('POST', '/matches', {
      name,
      config,
      ...(opponent === undefined ? {} : { opponent }),
    });
    this.adoptSession(result.session);
    return result;
  }

  /** Joins an existing match by its human-shareable match code. */
  async join(matchCode: string, name: string): Promise<{ session: Session; lobby: LobbyState }> {
    const result = await this.request<{ session: Session; lobby: LobbyState }>(
      'POST',
      `/matches/${encodeURIComponent(matchCode)}/join`,
      { name },
    );
    this.adoptSession(result.session);
    return result;
  }

  claimColony(colonyId: number): Promise<LobbyState> {
    return this.authedRequest<LobbyState>('POST', `/matches/${this.requireMatchId()}/claim`, { colonyId });
  }

  ready(ready: boolean): Promise<LobbyState> {
    return this.authedRequest<LobbyState>('POST', `/matches/${this.requireMatchId()}/ready`, { ready });
  }

  /** Host-only: locks config and advances `Lobby → Starting`. */
  async start(): Promise<void> {
    await this.authedRequest<void>('POST', `/matches/${this.requireMatchId()}/start`);
  }

  /** Host-only: discards the match. */
  async cancel(): Promise<void> {
    await this.authedRequest<void>('POST', `/matches/${this.requireMatchId()}/cancel`);
  }

  /** Polls the current roster/config — the lobby screen's refresh source. */
  poll(): Promise<LobbyState> {
    return this.authedRequest<LobbyState>('GET', `/matches/${this.requireMatchId()}/lobby`);
  }

  private adoptSession(session: Session): void {
    this._token = session.token;
    this._matchId = session.matchId;
  }

  private requireMatchId(): string {
    if (this._matchId === null) {
      throw new Error('LobbyClient: no active match — call create() or join() first');
    }
    return this._matchId;
  }

  private authedRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
    if (this._token === null) {
      throw new Error('LobbyClient: no session token — call create() or join() first');
    }
    return this.request<T>(method, path, body, this._token);
  }

  private async request<T>(method: string, path: string, body?: unknown, token?: string): Promise<T> {
    const headers: Record<string, string> = {};
    let payload: string | undefined;
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    if (token !== undefined) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, { method, headers, body: payload });
    if (!res.ok) {
      throw new LobbyHttpError(method, path, res.status);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }
}
